import { useEffect, useMemo, useState } from "react";
import { Dropdown, Drawer, Skeleton } from "../../components/ui";
import swal from "../../utils/swal";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Mail,
  MailOpen,
  MoreHorizontal,
  Search,
  SearchX,
} from "lucide-react";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  useDeleteNotification,
  useGetAllNotifications,
  useGetUnreadNotificationCount,
  useMarkAllNotificationsRead,
  useToggleNotificationRead,
} from "../../query/notificationQuery";
import DataBulkToolbar from "../../components/DataTable/DataBulkToolbar";
import DataTableCards from "../../components/DataTable/DataTableCards";
import { confirmBulkAction, runBulkIds, showBulkResult } from "../../utils/bulkActions";
import {
  NotificationDateCell,
  NotificationReadBadge,
  NotificationTitleCell,
  NotificationTypeBadge,
  NotificationTypeIcon,
} from "./NotificationParts";
import "../teachers/teachers.css";
import "./notifications.css";

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function AdminInbox() {
  const [searchTerm, setSearchTerm] = useState("");
  const [readFilter, setReadFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      isRead: readFilter,
      source: "SYSTEM",
    }),
    [paginationConfig, readFilter]
  );

  const { isLoading, isFetching, data } = useGetAllNotifications(queryParams);
  const { data: unreadCount = 0 } = useGetUnreadNotificationCount({ source: "SYSTEM" });
  const { mutate: toggleRead, mutateAsync: toggleReadAsync, isPending: isTogglingRead } =
    useToggleNotificationRead();
  const { mutate: deleteNotification, mutateAsync: deleteNotificationAsync } = useDeleteNotification();
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllNotificationsRead();

  const docs = data?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = data?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const hasQuery = Boolean(paginationConfig.keyword || readFilter);
  const allSelected = docs.length > 0 && docs.every((row) => selectedIds.includes(row._id));
  const liveActive = activeItem
    ? docs.find((row) => row._id === activeItem._id) || activeItem
    : null;

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  const handleToggleRead = (item) => {
    toggleRead({ id: item._id, isRead: !item.isRead });
  };

  const handleOpen = (item) => {
    setActiveItem(item);
    if (!item.isRead) toggleRead({ id: item._id, isRead: true });
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: `Delete "${item.title || "this notification"}"?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteNotification(item._id, {
        onSuccess: () => {
          setSelectedIds((prev) => prev.filter((id) => id !== item._id));
          if (activeItem?._id === item._id) setActiveItem(null);
          swal("Success", "Notification deleted", "success");
        },
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete notification", "error"),
      });
    });
  };

  const handleMarkAllRead = () => {
    if (!unreadCount) return;
    markAllRead(
      { source: "SYSTEM" },
      {
        onError: (error) => swal("Error", error?.message || "Failed to mark all as read", "error"),
      }
    );
  };

  const handleBulkRead = async (isRead) => {
    if (!selectedIds.length) return;
    const actionLabel = isRead ? "Mark as read" : "Mark as unread";
    const confirmed = await confirmBulkAction({
      actionLabel,
      count: selectedIds.length,
      entityLabel: "notifications",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, async (id) => {
      await toggleReadAsync({ id, isRead });
    });
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel, success, failed });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: "Delete selected",
      count: selectedIds.length,
      entityLabel: "notifications",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, deleteNotificationAsync);
    setSelectedIds([]);
    setIsBulkRunning(false);
    if (activeItem && selectedIds.includes(activeItem._id)) setActiveItem(null);
    showBulkResult({ actionLabel: "Delete", success, failed });
  };

  return (
    <div className="data-page inbox-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>

          <DataBulkToolbar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            disabled={isBulkRunning || isTogglingRead}
            actions={[
              { key: "read", label: "Mark as read", onClick: () => handleBulkRead(true) },
              { key: "unread", label: "Mark as unread", onClick: () => handleBulkRead(false) },
              { key: "delete", label: "Delete selected", tone: "danger", onClick: handleBulkDelete },
            ]}
          />
        </div>

        <div className="data-toolbar-actions inbox-toolbar-actions">
          <div className="inbox-filters">
            {[
              { value: "", label: "All" },
              { value: "false", label: "Unread" },
              { value: "true", label: "Read" },
            ].map((item) => (
              <button
                key={item.value || "all"}
                type="button"
                className={`inbox-filter${readFilter === item.value ? " is-active" : ""}`}
                onClick={() => {
                  setReadFilter(item.value);
                  setSelectedIds([]);
                  setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          {unreadCount > 0 ? (
            <button
              type="button"
              className="notif-mark-all-btn"
              disabled={isMarkingAll}
              onClick={handleMarkAllRead}
            >
              <MailOpen size={16} />
              Mark all read
            </button>
          ) : null}
        </div>
      </div>

      <div className="data-card">
        {isLoading && !data ? (
          <div style={{ padding: 24 }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {hasQuery ? <SearchX size={36} strokeWidth={1.6} /> : <Bell size={36} strokeWidth={1.6} />}
            </span>
            <h3>{hasQuery ? "No notifications found" : "No notifications yet"}</h3>
            <p>
              {hasQuery
                ? "Try a different keyword or filter."
                : "Realtime school alerts will appear here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isBulkRunning}>
              <table className="data-table inbox-table has-row-check">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="data-check"
                        checked={allSelected}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedIds((prev) => Array.from(new Set([...prev, ...docs.map((row) => row._id)])));
                          } else {
                            const pageIds = new Set(docs.map((row) => row._id));
                            setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
                          }
                        }}
                      />
                    </th>
                    <th className="inbox-col-title">Notification</th>
                    <th className="inbox-col-type">Type</th>
                    <th className="inbox-col-status">Status</th>
                    <th className="inbox-col-date">Received</th>
                    <th className="inbox-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const selected = selectedIds.includes(item._id);
                    const actionItems = [
                      {
                        key: "view",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => handleOpen(item),
                      },
                      {
                        key: "read",
                        label: (
                          <div style={{ padding: "8px 12px" }}>
                            {item.isRead ? (
                              <>
                                <Mail size={14} style={{ display: "inline", marginRight: 6 }} />
                                Mark unread
                              </>
                            ) : (
                              <>
                                <MailOpen size={14} style={{ display: "inline", marginRight: 6 }} />
                                Mark read
                              </>
                            )}
                          </div>
                        ),
                        onClick: () => handleToggleRead(item),
                      },
                      {
                        key: "delete",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr
                        key={item._id}
                        className={`notif-row${!item.isRead ? " is-unread" : ""}${selected ? " is-selected" : ""}`}
                        onClick={() => handleOpen(item)}
                      >
                        <td onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="data-check"
                            checked={selected}
                            onChange={(event) =>
                              setSelectedIds((prev) =>
                                event.target.checked
                                  ? [...prev, item._id]
                                  : prev.filter((id) => id !== item._id)
                              )
                            }
                          />
                        </td>
                        <td className="inbox-col-title">
                          <NotificationTitleCell notification={item} previewMax={140} />
                        </td>
                        <td className="inbox-col-type" onClick={(event) => event.stopPropagation()}>
                          <NotificationTypeBadge type={item.type} />
                        </td>
                        <td className="inbox-col-status" onClick={(event) => event.stopPropagation()}>
                          <NotificationReadBadge isRead={item.isRead} />
                        </td>
                        <td className="inbox-col-date">
                          <NotificationDateCell createdAt={item.createdAt} />
                        </td>
                        <td className="inbox-col-action" onClick={(event) => event.stopPropagation()}>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} of {totalDocs} notifications
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={Boolean(liveActive)}
        onClose={() => setActiveItem(null)}
        placement="right"
        title="Notification"
        width={440}
        footer={
          liveActive ? (
            <>
              <button
                type="button"
                className="bw-drawer-btn is-ghost"
                onClick={() => handleToggleRead(liveActive)}
                disabled={isTogglingRead}
              >
                {liveActive.isRead ? "Mark unread" : "Mark read"}
              </button>
              <button
                type="button"
                className="bw-drawer-btn is-danger"
                onClick={() => handleDelete(liveActive)}
              >
                Delete
              </button>
            </>
          ) : null
        }
      >
        {liveActive ? (
          <div className="inbox-detail">
            <div className="inbox-detail-head">
              <NotificationTypeIcon type={liveActive.type} size={20} />
              <div className="inbox-detail-copy">
                <h3>{liveActive.title || "Untitled notification"}</h3>
                <div className="inbox-detail-meta">
                  <NotificationTypeBadge type={liveActive.type} />
                  <NotificationReadBadge isRead={liveActive.isRead} />
                </div>
              </div>
            </div>
            <NotificationDateCell createdAt={liveActive.createdAt} />
            <p className="inbox-detail-body">{liveActive.content || "No additional details."}</p>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

export default AdminInbox;
